"use client";

import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
import { CheckCircleIcon } from "staysure-component-library";

import ToggleBtnLabelWrapper from "@/components/atoms/toggle-btn-wrapper/toggle-btn-label-wrapper";
import withBrand from "@/components/with-brand";

import Styles from "./trip-type-toggle.module.css";

type TripTypeToggleProps = Readonly<{
  firstOptionId: string;
  firstOptionLabel: string;
  firstOptionValue: string;
  firstOptionDescription?: string | React.ReactElement;
  firstOptionIcon?: React.ReactElement;
  firstOptionActiveIcon?: React.ReactElement;
  secondOptionId: string;
  secondOptionLabel: string;
  secondOptionValue: string;
  secondOptionDescription?: string | React.ReactElement;
  secondOptionIcon?: React.ReactElement;
  secondOptionActiveIcon?: React.ReactElement;
  defaultActiveValue?: string;
  onToggle: (value: string) => void;
  isDisabled?: boolean;
  className?: string;
  "data-testid"?: string;
  brand?: "staysure" | "avanti";
  isSideBySide?: boolean;
}>;

function TripTypeToggle({
  firstOptionId,
  firstOptionLabel,
  firstOptionValue,
  firstOptionDescription,
  firstOptionIcon,
  firstOptionActiveIcon,
  secondOptionId,
  secondOptionLabel,
  secondOptionValue,
  secondOptionDescription,
  secondOptionIcon,
  secondOptionActiveIcon,
  defaultActiveValue = "",
  onToggle,
  isDisabled = false,
  className = "",
  "data-testid": dataTestId,
  brand,
  isSideBySide,
}: TripTypeToggleProps) {
  const [activeValue, setActiveValue] = useState(defaultActiveValue);

  const brandClassMap: Record<"staysure" | "avanti", string> = {
    staysure: Styles.trip_toggle_sts,
    avanti: Styles.trip_toggle_avn,
  };

  const brandClass = isSideBySide && brand ? brandClassMap[brand] : "";

  // Helper function to parse HTML strings into React elements
  const parseDescription = useCallback(
    (description: string | React.ReactElement | undefined) => {
      if (!description) return undefined;
      if (typeof description === "string") {
        // Check if the string contains HTML tags
        if (description.includes("<") && description.includes(">")) {
          return parse(description) as React.ReactElement;
        }
        return description;
      }
      return description;
    },
    []
  );

  useEffect(() => {
    setActiveValue(defaultActiveValue);
  }, [defaultActiveValue]);

  const handleFirstOptionClick = useCallback(
    (isActive: boolean | React.MouseEvent) => {
      const active = typeof isActive === "boolean" ? isActive : true;
      if (active && activeValue !== firstOptionValue) {
        setActiveValue(firstOptionValue);
        onToggle(firstOptionValue);
      }
    },
    [activeValue, firstOptionValue, onToggle]
  );

  const handleSecondOptionClick = useCallback(
    (isActive: boolean | React.MouseEvent) => {
      const active = typeof isActive === "boolean" ? isActive : true;
      if (active && activeValue !== secondOptionValue) {
        setActiveValue(secondOptionValue);
        onToggle(secondOptionValue);
      }
    },
    [activeValue, secondOptionValue, onToggle]
  );

  // Reusable render function for toggle button
  const renderToggleButton = useCallback(
    (
      id: string,
      label: string,
      value: string,
      description: string | React.ReactElement | undefined,
      icon: React.ReactElement | undefined,
      activeIcon: React.ReactElement | undefined,
      onClick: (isActive: boolean | React.MouseEvent) => void
    ) => {
      const isActive = activeValue === value;
      const baseClassName = `
       active:bg-interactive-secondary-default active:text-dark-text-on-primary border-[1px]
                            bg-surface-neutral-option-1
                            border-interactive-secondary-default
                            text-text-on-primary w-full  tablet:w-[328px] tablet:max-w-[328px] rounded-lg text-left font-body-font leading-[24px] py-spacing-s px-spacing-s hover:px-[11px] flex flex-col justify-center self-start gap-grid-system-space-050 ${Styles.policy_togglebtn_wrapper} ${Styles.toggle_btn} ${brandClass} hover:bg-interactive-action-hovered

      `;

      const parsedDescription = parseDescription(description);

      if (icon) {
        return (
          <ToggleBtnLabelWrapper
            id={id}
            btnType="button"
            label={label}
            description={parsedDescription}
            isDefaultActive={isActive}
            onClick={onClick}
            isDisabled={isDisabled}
            isToggleEnable={false}
            className={baseClassName}
            isActiveRightIcon={true}
            activeRightIcon={
              <CheckCircleIcon stroke="#306582" fill="#ffffff" />
            }
            isLeftIcon={true}
            leftIcon={icon}
            activeLeftIcon={activeIcon || icon}
          />
        );
      }

      return (
        <ToggleBtnLabelWrapper
          id={id}
          btnType="button"
          label={label}
          description={parsedDescription}
          isDefaultActive={isActive}
          onClick={onClick}
          isDisabled={isDisabled}
          isToggleEnable={false}
          className={baseClassName}
          isActiveRightIcon={true}
          activeRightIcon={<CheckCircleIcon stroke="#306582" fill="#ffffff" />}
        />
      );
    },
    [activeValue, isDisabled, parseDescription]
  );

  return (
    <div
      className={`flex flex-col tablet:flex-row gap-spacing-xs tablet:gap-spacing-m items-center tablet:justify-center md:mx-10 tablet:mx-0  sm:mx-4 ${brandClass} ${className}`}
      data-testid={dataTestId}
    >
      {renderToggleButton(
        firstOptionId,
        firstOptionLabel,
        firstOptionValue,
        firstOptionDescription,
        firstOptionIcon,
        firstOptionActiveIcon,
        handleFirstOptionClick
      )}

      {renderToggleButton(
        secondOptionId,
        secondOptionLabel,
        secondOptionValue,
        secondOptionDescription,
        secondOptionIcon,
        secondOptionActiveIcon,
        handleSecondOptionClick
      )}
    </div>
  );
}

export default withBrand(TripTypeToggle);
